<script setup>
/* global axios */
// PLUZE-CUSTOM: EMAIL-SEND-RESOLVE
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], required: true },
  messages: { type: Array, default: () => [] },
});
const { t } = useI18n();
const selected = ref(false);
const loading = ref(false);
const context = ref(null);
const error = ref('');
const classification = ref('');
const result = ref('');
const reason = ref('');
const isSpam = ref(false);
let generation = 0;
const receipt = computed(() => {
  const message = [...props.messages]
    .reverse()
    .find(item => item.content_attributes?.pluze_email_resolution_status);
  if (!message) return null;
  if (message.status === 'failed') return { state: 'failed' };
  return message.content_attributes.pluze_email_resolution_status;
});
function reset() {
  generation += 1;
  selected.value = false;
  loading.value = false;
  context.value = null;
  error.value = '';
  classification.value = '';
  result.value = '';
  reason.value = '';
  isSpam.value = false;
}
watch(() => [props.accountId, props.conversationId], reset);
async function load() {
  if (!selected.value) return;
  generation += 1;
  const request = generation;
  loading.value = true;
  error.value = '';
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${props.accountId}/conversations/${props.conversationId}/pluze_resolution`
    );
    if (request !== generation) return;
    context.value = data;
    classification.value = data.classification || '';
    if (data.resolved)
      error.value = t('CONVERSATION.PLUZE_RESOLUTION.ALREADY_RESOLVED');
    else if (data.blockers.length) error.value = data.blockers.join('; ');
  } catch (e) {
    if (request === generation)
      error.value =
        e.response?.data?.error ||
        t('CONVERSATION.PLUZE_RESOLUTION.UNAVAILABLE');
  } finally {
    if (request === generation) loading.value = false;
  }
}
function prepare(message) {
  if (!selected.value) return null;
  if (loading.value || !context.value || error.value)
    throw new Error(
      error.value || t('CONVERSATION.PLUZE_RESOLUTION.UNAVAILABLE')
    );
  if (isSpam.value) {
    return {
      ticket_id: context.value.ticket_id,
      classificacao: 'Spam',
      resumo: 'Atendimento classificado como spam.',
      justificativa_sem_unidade: 'Spam',
    };
  }
  const summary = result.value.trim() || message.trim();
  if (
    !classification.value.trim() ||
    summary.length < 3 ||
    summary.length > 8000 ||
    (context.value.needs_unit_reason && !reason.value.trim())
  ) {
    throw new Error(t('CONVERSATION.PLUZE_RESOLUTION.REQUIRED'));
  }
  return {
    ticket_id: context.value.ticket_id,
    classificacao: classification.value.trim(),
    resumo: summary,
    justificativa_sem_unidade: reason.value.trim(),
  };
}
async function select() {
  selected.value = true;
  await load();
}
defineExpose({ prepare, reset, select });
// PLUZE-CUSTOM-END: EMAIL-SEND-RESOLVE
</script>

<template>
  <div class="px-3 py-2 border-t border-n-weak text-sm">
    <p v-if="receipt" class="mb-2 text-n-slate-11" role="status">
      {{
        receipt.detail ||
        t(`CONVERSATION.PLUZE_RESOLUTION.STATE_${receipt.state.toUpperCase()}`)
      }}
    </p>
    <label class="flex items-center gap-2 cursor-pointer">
      <input v-model="selected" type="checkbox" class="m-0" @change="load" />
      <span>{{ t('CONVERSATION.PLUZE_RESOLUTION.LABEL') }}</span>
    </label>
    <div v-if="selected" class="mt-2 flex flex-col gap-2">
      <p class="text-xs text-n-slate-11">
        {{ t('CONVERSATION.PLUZE_RESOLUTION.HELP') }}
      </p>
      <p v-if="loading" role="status">
        {{ t('CONVERSATION.PLUZE_RESOLUTION.LOADING') }}
      </p>
      <p v-if="error" role="alert" class="text-n-ruby-11">{{ error }}</p>
      <template v-if="context && !error">
        <label class="flex items-center gap-2 cursor-pointer">
          <input v-model="isSpam" type="checkbox" class="m-0" />
          <span>{{ t('CONVERSATION.PLUZE_RESOLUTION.SPAM_LABEL') }}</span>
        </label>
        <p v-if="isSpam" class="text-xs text-n-slate-11">
          {{ t('CONVERSATION.PLUZE_RESOLUTION.SPAM_HELP') }}
        </p>
        <template v-else>
          <label>
            {{ t('CONVERSATION.PLUZE_RESOLUTION.CLASSIFICATION') }} ·
            {{ context.protocol }}
            <input
              v-model="classification"
              type="text"
              maxlength="255"
              class="mb-0"
            />
          </label>
          <label>
            {{ t('CONVERSATION.PLUZE_RESOLUTION.RESULT') }}
            <textarea
              v-model="result"
              maxlength="8000"
              rows="2"
              class="mb-0"
              :placeholder="t('CONVERSATION.PLUZE_RESOLUTION.RESULT_HINT')"
            />
          </label>
          <label v-if="context.needs_unit_reason">
            {{ t('CONVERSATION.PLUZE_RESOLUTION.REASON') }}
            <input v-model="reason" type="text" maxlength="1000" class="mb-0" />
          </label>
        </template>
      </template>
    </div>
  </div>
</template>
